// src/components/ProgramsSection.jsx
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SequentialFilters from "./SequentialFilters";

const STATUS_LABEL = {
  open: 'Ανοιχτό',
  upcoming: 'Επερχόμενο',
  closed: 'Ολοκληρωμένο',
};

const TYPE_LABEL = {
  all: 'Όλα',
  voucher: 'Voucher',
  selfFunded: 'Αυτοχρηματοδοτούμενο',
};

const AUDIENCE_LABEL = {
  unemployed: 'Άνεργοι',
  employed: 'Εργαζόμενοι',
};

function safeDateLabel(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('el-GR');
}

function dateValue(iso, fallback = 0) {
  if (!iso) return fallback;
  const d = new Date(iso);
  const t = d.getTime();
  return Number.isNaN(t) ? fallback : t;
}

function daysBetween(a, b) {
  const ms = Math.abs(a - b);
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

function isNewProgram(startDateIso, windowDays = 14) {
  const t = dateValue(startDateIso, NaN);
  if (Number.isNaN(t)) return false;
  const now = Date.now();
  return t <= now && daysBetween(now, t) <= windowDays;
}

function statusPill(status) {
  const base = 'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold border';
  if (status === 'open') return `${base} bg-emerald-50 text-emerald-700 border-emerald-200`;
  if (status === 'upcoming') return `${base} bg-amber-50 text-amber-700 border-amber-200`;
  return `${base} bg-gray-50 text-gray-700 border-gray-200`;
}

export default function ProgramsSection() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  // typeFilter: all | voucher | selfFunded
  const [typeFilter, setTypeFilter] = useState('all');
  const [voucherAudienceFilter, setVoucherAudienceFilter] = useState('unemployed'); // unemployed | employed
  const [statusFilter, setStatusFilter] = useState('all'); // all | open | upcoming | closed

  useEffect(() => {
    const raw = sessionStorage.getItem("pendingProgramsFilters");
    if (!raw) return;

    try {
      const d = JSON.parse(raw);
      sessionStorage.removeItem("pendingProgramsFilters");

      if (d.typeFilter) setTypeFilter(d.typeFilter);
      if (typeof d.voucherAudienceFilter !== "undefined") setVoucherAudienceFilter(d.voucherAudienceFilter);
      if (d.statusFilter) setStatusFilter(d.statusFilter);

      setTimeout(() => {
        document.getElementById("programs")?.scrollIntoView({ behavior: "smooth" });
      }, 150);
    } catch {
      sessionStorage.removeItem("pendingProgramsFilters");
    }
  }, []);

  useEffect(() => {
    function onSetFilters(e) {
      const d = e?.detail || {};
      if (d.typeFilter) setTypeFilter(d.typeFilter);
      if (typeof d.voucherAudienceFilter !== "undefined") setVoucherAudienceFilter(d.voucherAudienceFilter);
      if (d.statusFilter) setStatusFilter(d.statusFilter);
    }

    window.addEventListener("programs:setFilters", onSetFilters);
    return () => window.removeEventListener("programs:setFilters", onSetFilters);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);

        const indexRes = await fetch('/articles/index.json', { cache: 'no-store' });
        if (!indexRes.ok) throw new Error('Failed to load /articles/index.json');
        const index = await indexRes.json();

        const paths = Array.isArray(index.items) ? index.items : [];
        const results = await Promise.all(
          paths.map(async (p) => {
            const res = await fetch(p, { cache: 'no-store' });
            if (!res.ok) throw new Error(`Failed to load ${p}`);
            return res.json();
          })
        );

        if (!cancelled) setArticles(results);
      } catch (e) {
        if (!cancelled) setArticles([]);
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Normalize audience only when filtering vouchers explicitly.
  useEffect(() => {
    if (typeFilter !== 'voucher') return;
    if (voucherAudienceFilter !== 'unemployed' && voucherAudienceFilter !== 'employed') {
      setVoucherAudienceFilter('unemployed');
    }
  }, [typeFilter, voucherAudienceFilter]);

  // ✅ FILTERING RULES:
  // - If typeFilter === 'all': show ALL ACTIVE programs (open + upcoming), ignoring audience & statusFilter
  // - Else: apply normal filters (type + audience-for-voucher + statusFilter)
// A) Για τα cards (featured): όταν typeFilter === 'all' -> ΜΟΝΟ ενεργά (open + upcoming) και αγνοεί ΟΛΑ τα άλλα φίλτρα
const filteredForCards = useMemo(() => {
  if (typeFilter === 'all') {
    return articles.filter((a) => a.status === 'open' || a.status === 'upcoming');
  }

  return articles
    .filter((a) => (typeFilter ? a.type === typeFilter : true))
    .filter((a) => {
      if (typeFilter !== 'voucher') return true;
      return a.voucherAudience === voucherAudienceFilter;
    })
    .filter((a) => (statusFilter === 'all' ? true : a.status === statusFilter));
}, [articles, typeFilter, voucherAudienceFilter, statusFilter]);

// B) Για τον πίνακα (κάτω λίστα): όταν typeFilter === 'all' -> δείχνει ΟΛΑ (open + upcoming + closed), αγνοεί audience/status filters
const filteredForTable = useMemo(() => {
  if (typeFilter === 'all') {
    return articles; // εδώ κρατάμε και τα ολοκληρωμένα
  }

  return articles
    .filter((a) => (typeFilter ? a.type === typeFilter : true))
    .filter((a) => {
      if (typeFilter !== 'voucher') return true;
      return a.voucherAudience === voucherAudienceFilter;
    })
    .filter((a) => (statusFilter === 'all' ? true : a.status === statusFilter));
}, [articles, typeFilter, voucherAudienceFilter, statusFilter]);
 

// Cards use filteredForCards
const openItems = useMemo(() => {
  return filteredForCards
    .filter((a) => a.status === 'open')
    .sort((x, y) => dateValue(x.endDate, Infinity) - dateValue(y.endDate, Infinity));
}, [filteredForCards]);

const upcomingItems = useMemo(() => {
  return filteredForCards
    .filter((a) => a.status === 'upcoming')
    .sort((x, y) => dateValue(x.startDate, Infinity) - dateValue(y.startDate, Infinity));
}, [filteredForCards]);

// Table uses filteredForTable (ώστε να κρατάει και closed στο all)
const closedItems = useMemo(() => {
  return filteredForTable
    .filter((a) => a.status === 'closed')
    .sort((x, y) => dateValue(y.endDate, 0) - dateValue(x.endDate, 0));
}, [filteredForTable]);

  const featured = useMemo(() => {
    if (loading) return [];
    const base =
      // When 'all', we already have only active items inside filtered.
      typeFilter === 'all'
        ? [...openItems, ...upcomingItems]
        : statusFilter === 'open'
        ? openItems
        : statusFilter === 'upcoming'
        ? upcomingItems
        : statusFilter === 'closed'
        ? []
        : [...openItems, ...upcomingItems];

    return base
      .slice()
      .sort((x, y) => {
        const rank = (s) => (s === 'open' ? 1 : s === 'upcoming' ? 2 : 9);
        return rank(x.status) - rank(y.status) || dateValue(x.startDate, Infinity) - dateValue(y.startDate, Infinity);
      })
      .slice(0, 6);
  }, [openItems, upcomingItems, statusFilter, loading, typeFilter]);

 const openItemsTable = useMemo(() => {
  return filteredForTable
    .filter((a) => a.status === 'open')
    .sort((x, y) => dateValue(x.endDate, Infinity) - dateValue(y.endDate, Infinity));
}, [filteredForTable]);

const upcomingItemsTable = useMemo(() => {
  return filteredForTable
    .filter((a) => a.status === 'upcoming')
    .sort((x, y) => dateValue(x.startDate, Infinity) - dateValue(y.startDate, Infinity));
}, [filteredForTable]);

const tableRows = useMemo(() => {
  const all = [...openItemsTable, ...upcomingItemsTable, ...closedItems];
  return all.sort((x, y) => {
    const statusOrder = { open: 1, upcoming: 2, closed: 3 };
    return (
      (statusOrder[x.status] ?? 9) - (statusOrder[y.status] ?? 9) ||
      dateValue(x.startDate, Infinity) - dateValue(y.startDate, Infinity)
    );
  });
}, [openItemsTable, upcomingItemsTable, closedItems]);

  return (
    <section id="programs" className="bg-white">
      <div className="container mx-auto px-4 py-10">
        <div className="text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900">
            Προγράμματα & Δράσεις
          </h2>
        </div>

        <SequentialFilters
          value={{ typeFilter, voucherAudienceFilter, statusFilter }}
          onChange={({ typeFilter, voucherAudienceFilter, statusFilter }) => {
            setTypeFilter(typeFilter);
            setVoucherAudienceFilter(voucherAudienceFilter);
            setStatusFilter(statusFilter);
          }}
          className="mt-8"
        />

        <div className="mt-10">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h3 className="text-xl sm:text-2xl font-extrabold text-gray-900">Νέα / Προτεινόμενα</h3>

            <div className="text-sm text-gray-600">
              Εμφάνιση: {TYPE_LABEL[typeFilter] || '—'}
              {typeFilter === 'voucher' ? ` • ${AUDIENCE_LABEL[voucherAudienceFilter] || '—'}` : ''}
              {typeFilter !== 'all' && statusFilter !== 'all' ? ` • ${STATUS_LABEL[statusFilter] || '—'}` : ''}
              {typeFilter === 'all' ? ' • Ενεργά (Ανοιχτά + Επερχόμενα)' : ''}
            </div>
          </div>

          {loading ? (
            <div className="mt-6 rounded-2xl border border-gray-200 bg-gray-50 p-8 text-center text-gray-600">
              Φόρτωση προγραμμάτων…
            </div>
          ) : featured.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-gray-200 bg-gray-50 p-8 text-center text-gray-600">
              Δεν βρέθηκαν προγράμματα για τα φίλτρα που επέλεξες.
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              {featured.map((a) => (
                <article
                  key={a.slug}
                  className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm hover:shadow-md transition"
                >
                  <div className="relative">
                    <img
                      src={a.image || '/1.jpg'}
                      alt={a.title}
                      className="w-full aspect-[16/9] object-cover"
                      loading="lazy"
                    />

                    <div className="absolute top-3 right-3 flex gap-2">
                      {isNewProgram(a.startDate, 30) ? (
                        <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-extrabold bg-blue-600 text-white shadow-sm">
                          ΝΕΟ
                        </span>
                      ) : null}
                      <span className={statusPill(a.status)}>{STATUS_LABEL[a.status] || a.status}</span>
                    </div>
                  </div>

                  <div className="p-5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full">
                        {TYPE_LABEL[a.type] || '—'}
                      </span>
                      {a.type === 'voucher' && a.voucherAudience ? (
                        <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full">
                          {AUDIENCE_LABEL[a.voucherAudience] || '—'}
                        </span>
                      ) : null}
                    </div>

                    <h4 className="mt-3 text-lg font-extrabold text-gray-900 leading-snug">{a.title}</h4>
                    <p className="mt-2 text-sm text-gray-600 leading-relaxed">{a.summary || ''}</p>

                    <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                      <div className="rounded-xl bg-gray-50 border border-gray-200 p-3">
                        <div className="text-gray-500 text-xs font-semibold">Έναρξη</div>
                        <div className="mt-1 font-semibold text-gray-900">{safeDateLabel(a.startDate)}</div>
                      </div>
                      <div className="rounded-xl bg-gray-50 border border-gray-200 p-3">
                        <div className="text-gray-500 text-xs font-semibold">Λήξη</div>
                        <div className="mt-1 font-semibold text-gray-900">{safeDateLabel(a.endDate)}</div>
                      </div>
                    </div>

                    <div className="mt-5">
                      <Link
                        to={`/programs/${a.slug}`}
                        className="inline-flex w-full items-center justify-center rounded-2xl bg-blue-600 px-5 py-3 text-white font-semibold hover:bg-blue-700 transition"
                      >
                        Δείτε Λεπτομέρειες
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        <div className="mt-14">
          <h3 className="text-xl sm:text-2xl font-extrabold text-gray-900">Αναλυτικός Πίνακας Προγραμμάτων</h3>

          <div className="mt-6 rounded-2xl border border-gray-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-[15px] sm:text-base">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left text-gray-700">
                    <th className="px-4 py-3 font-semibold">Τίτλος</th>
                    <th className="px-4 py-3 font-semibold">Κατηγορία</th>
                    <th className="px-4 py-3 font-semibold">Κοινό</th>
                    <th className="px-4 py-3 font-semibold">Κατάσταση</th>
                    <th className="px-4 py-3 font-semibold">Έναρξη</th>
                    <th className="px-4 py-3 font-semibold">Λήξη</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td className="px-4 py-6 text-gray-600" colSpan={6}>
                        Φόρτωση…
                      </td>
                    </tr>
                  ) : tableRows.length === 0 ? (
                    <tr>
                      <td className="px-4 py-6 text-gray-600" colSpan={6}>
                        Δεν υπάρχουν προγράμματα.
                      </td>
                    </tr>
                  ) : (
                    tableRows.map((a) => (
                      <tr key={a.slug} className="hover:bg-gray-50 transition">
                        <td className="px-4 py-4">
                          <Link to={`/programs/${a.slug}`} className="font-semibold text-gray-900 hover:underline">
                            {a.title}
                          </Link>
                          {a.summary ? <div className="mt-1 text-xs text-gray-500 line-clamp-2">{a.summary}</div> : null}
                        </td>
                        <td className="px-4 py-4 text-gray-700">{TYPE_LABEL[a.type] || '—'}</td>
                        <td className="px-4 py-4 text-gray-700">
                          {a.type === 'voucher' ? (AUDIENCE_LABEL[a.voucherAudience] || '—') : '—'}
                        </td>
                        <td className="px-4 py-4">
                          <span className={statusPill(a.status)}>{STATUS_LABEL[a.status] || '—'}</span>
                        </td>
                        <td className="px-4 py-4 text-gray-700">{safeDateLabel(a.startDate)}</td>
                        <td className="px-4 py-4 text-gray-700">{safeDateLabel(a.endDate)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-4 text-xs text-gray-500">
            Σημείωση: Τα links οδηγούν σε <span className="font-semibold">/programs/:slug</span>.
          </div>
        </div>
      </div>
    </section>
  );
}